import React, { useState, useCallback, useRef, useLayoutEffect } from 'react'
import { Dropdown, Popover } from 'antd'
import { Icon } from '@iconify/react'
import Picker from '@emoji-mart/react'
import emojiData from '@emoji-mart/data'
import styles from './MentionEditorInsertMenu.module.css'

const ICON_SIZE = 16
const MENU_ICON_STYLE = { color: '#757677', marginRight: 8, flexShrink: 0 }

/**
 * Shared insert dropdown (task context menu design).
 * @param {object} props
 * @param {Array} props.sections - `{ items: [{ key, label, icon }] }[]`
 * @param {(key: string) => boolean|void} props.onInsertItem - return `false` to keep menu open
 * @param {string} props.plusButtonClassName
 * @param {string} [props.wrapClassName]
 * @param {string} [props.buttonTitle]
 * @param {string} [props.placement]
 * @param {boolean} [props.showLinkPopover]
 * @param {object} [props.emoji] - `{ open, onOpenChange, onSelect, savedRangeRef }`
 * @param {object} [props.link] - `{ open, state, onStateChange, onSubmit, onClose, quillRef }`
 * @param {object} [props.image] - `{ inputRef, onChange }`
 */
const QuillInsertDropdown = ({
    sections,
    onInsertItem,
    plusButtonClassName,
    wrapClassName = '',
    buttonTitle = 'Insert',
    placement = 'topLeft',
    showLinkPopover = true,
    emoji,
    link,
    image,
}) => {
    const linkUrlInputRef = useRef(null)
    const linkTextInputRef = useRef(null)
    const internalEmojiRangeRef = useRef(null)

    const [insertOpen, setInsertOpen] = useState(false)

    const emojiOpen = emoji?.open ?? false
    const savedEmojiRange = emoji?.savedRangeRef ?? internalEmojiRangeRef

    const setEmojiOpen = useCallback(
        (open) => {
            if (open) emoji?.onOpenChange?.(true)
            else {
                emoji?.onOpenChange?.(false)
                emoji?.onClose?.()
            }
        },
        [emoji]
    )

    const linkPopup = link?.state ?? {
        open: false,
        text: '',
        url: '',
        savedRange: null,
    }
    const setLinkPopup = link?.onStateChange

    const closeInsertMenu = useCallback(() => setInsertOpen(false), [])

    const closeLinkPopup = useCallback(() => {
        link?.onClose?.()
        link?.quillRef?.current?.focus()
    }, [link])

    const handleLinkOpenFromInsert = useCallback(() => {
        const q = link?.quillRef?.current
        if (!q || !setLinkPopup) return
        const range = q.getSelection()
        const fmt = range ? q.getFormat(range) : {}
        const selectedText = range?.length
            ? q.getText(range.index, range.length)
            : ''
        closeInsertMenu()
        setLinkPopup({
            open: true,
            text: selectedText,
            url: fmt.link || '',
            savedRange: range,
        })
    }, [link, closeInsertMenu, setLinkPopup])

    const handleLinkSubmit = useCallback(() => {
        if (link?.onSubmit) {
            link.onSubmit()
            return
        }
        closeLinkPopup()
    }, [link, closeLinkPopup])

    const handleLinkKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                handleLinkSubmit()
            }
            if (e.key === 'Escape') {
                e.preventDefault()
                closeLinkPopup()
            }
        },
        [handleLinkSubmit, closeLinkPopup]
    )

    useLayoutEffect(() => {
        if (!linkPopup.open) return
        const focusTarget = linkPopup.text
            ? linkUrlInputRef.current
            : linkTextInputRef.current
        setTimeout(() => focusTarget?.focus(), 0)
        // Focus only when the popover opens; do not re-run when text/url change while typing.
    }, [linkPopup.open])

    const handleEmojiSelect = useCallback(
        (emojiNative) => {
            emoji?.onSelect?.(emojiNative)
        },
        [emoji]
    )

    const handleItemClick = useCallback(
        (key) => {
            if (key === 'link' && showLinkPopover && link) {
                handleLinkOpenFromInsert()
                return
            }
            const keepOpen = onInsertItem(key)
            if (keepOpen !== false) {
                setInsertOpen(false)
            }
        },
        [onInsertItem, showLinkPopover, link, handleLinkOpenFromInsert]
    )

    const insertMenuContent = (
        <div className={styles.insertMenu}>
            <div className={styles.insertMenuLabel}>Insert</div>
            {sections.map((section, sectionIndex) => (
                <div key={sectionIndex} className={styles.insertMenuSection}>
                    {section.items.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            className={styles.insertMenuItem}
                            onMouseDown={(e) => {
                                e.preventDefault()
                                handleItemClick(item.key)
                            }}
                        >
                            <Icon
                                icon={item.icon}
                                width={16}
                                height={16}
                                style={MENU_ICON_STYLE}
                            />
                            <span>{item.label}</span>
                        </button>
                    ))}
                </div>
            ))}
        </div>
    )

    const linkPopoverContent = showLinkPopover && link && setLinkPopup && (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <input
                ref={linkTextInputRef}
                className={styles.linkInput}
                value={linkPopup.text}
                onChange={(e) =>
                    setLinkPopup((s) => ({ ...s, text: e.target.value }))
                }
                onKeyDown={handleLinkKeyDown}
                placeholder={link?.textPlaceholder ?? 'Text'}
                type="text"
            />
            <input
                ref={linkUrlInputRef}
                className={styles.linkInput}
                value={linkPopup.url}
                onChange={(e) =>
                    setLinkPopup((s) => ({ ...s, url: e.target.value }))
                }
                onKeyDown={handleLinkKeyDown}
                placeholder={link?.urlPlaceholder ?? 'URL'}
                type="url"
            />
            <div className={styles.linkActions}>
                <button
                    type="button"
                    className={styles.linkCancelBtn}
                    onMouseDown={(e) => {
                        e.preventDefault()
                        closeLinkPopup()
                    }}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className={styles.linkSubmitBtn}
                    onMouseDown={(e) => {
                        e.preventDefault()
                        handleLinkSubmit()
                    }}
                >
                    Apply
                </button>
            </div>
        </div>
    )

    const plusButton = (
        <button
            type="button"
            className={plusButtonClassName}
            onMouseDown={(e) => e.preventDefault()}
            title={buttonTitle}
        >
            <Icon icon="mdi:plus" width={ICON_SIZE} height={ICON_SIZE} />
        </button>
    )

    const dropdown = (
        <Dropdown
            trigger={['click']}
            placement={placement}
            open={insertOpen}
            onOpenChange={setInsertOpen}
            overlayClassName={styles.insertDropdown}
            popupRender={() => insertMenuContent}
            getPopupContainer={() => document.body}
            destroyOnHidden
        >
            {plusButton}
        </Dropdown>
    )

    let wrapped = dropdown

    if (emoji) {
        wrapped = (
            <Popover
                open={emojiOpen}
                onOpenChange={setEmojiOpen}
                trigger={[]}
                placement={placement}
                classNames={{
                    root: styles.emojiPopover,
                }}
                getPopupContainer={() => document.body}
                content={
                    <div onMouseDown={(e) => e.stopPropagation()}>
                        <Picker
                            data={emojiData}
                            onEmojiSelect={handleEmojiSelect}
                            theme="light"
                            previewPosition="bottom"
                            skinTonePosition="search"
                            autoFocus
                        />
                    </div>
                }
                destroyOnHidden
            >
                {wrapped}
            </Popover>
        )
    }

    if (showLinkPopover && link && setLinkPopup) {
        wrapped = (
            <Popover
                open={linkPopup.open}
                onOpenChange={(open) => {
                    if (!open) closeLinkPopup()
                }}
                trigger={[]}
                placement={placement}
                overlayClassName={styles.linkPopover}
                getPopupContainer={() => document.body}
                content={linkPopoverContent}
                destroyOnHidden
            >
                {wrapped}
            </Popover>
        )
    }

    return (
        <>
            <span className={wrapClassName}>{wrapped}</span>
            {image?.inputRef && (
                <input
                    ref={image.inputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml,image/bmp,image/x-icon,.png,.jpg,.jpeg,.gif,.webp,.svg,.bmp,.ico"
                    style={{ display: 'none' }}
                    onChange={image.onChange}
                />
            )}
        </>
    )
}

export default QuillInsertDropdown
